import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { owner, plansGuest, plansStripe } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Check, Sparkles, Zap, Award, Loader2, Star, ArrowRight, CreditCard } from 'lucide-react';
import PayPalTrustBadge from '../components/PayPalTrustBadge';
import { PlanCompareTable } from '../components/PlanCompareTable';
import { PlanFAQ } from '../components/PlanFAQ';

const ICONS = { basic: Award, basic_annual: Award, standard: Zap, featured: Sparkles };
// Order: Tier 1 (6mo + 12mo), then Growth + Full Package.
const ORDER = ['basic', 'basic_annual', 'standard', 'featured'];
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
// Tier 1 uses Stripe recurring subscriptions.
const STRIPE_TIER1 = new Set(['basic', 'basic_annual']);

// A stripped-down guest-friendly version of UpgradePlan.jsx. Guests pay first,
// then get bounced into /signup with a purchase token so their new account
// automatically inherits the plan they just paid for.
export default function Plans() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPkg, setOpenPkg] = useState(null);
  const { user } = useAuth();
  const [params] = useSearchParams();
  const needsSub = params.get('reason') === 'listing';

  useEffect(() => {
    owner.packages()
      .then(p => {
        const sorted = ORDER.map(id => p.find(x => x.id === id)).filter(Boolean);
        setPackages(sorted);
      })
      .catch(() => toast({ title: 'Could not load plans', description: 'Please refresh the page.' }))
      .finally(() => setLoading(false));
  }, []);

  const current = useMemo(() => packages.find(p => p.id === openPkg), [openPkg, packages]);

  const onPaymentSuccess = async ({ purchaseToken, package: pkgId }) => {
    if (user) {
      setOpenPkg(null);
      try {
        await plansGuest.claim(purchaseToken);
        toast({ title: 'Payment received!', description: "Your plan is active. Let's create your listing." });
        navigate('/business/manage?create=1');
      } catch (err) {
        toast({ title: 'Payment received, but we could not attach it', description: 'Please contact support with your PayPal receipt.' });
      }
      return;
    }
    // Stash so the signup form can pre-populate + attach the token in POST body.
    try { sessionStorage.setItem('pbb_plan_purchase_token', purchaseToken); } catch {}
    try { sessionStorage.setItem('pbb_plan_purchase_pkg', pkgId); } catch {}
    toast({ title: 'Payment received!', description: 'Now finish creating your account.' });
    setOpenPkg(null);
    navigate(`/signup?planToken=${encodeURIComponent(purchaseToken)}&plan=${encodeURIComponent(pkgId)}`);
  };

  const buy = async (t) => {
    if (STRIPE_TIER1.has(t.id)) {
      const key = t.id === 'basic_annual' ? 'pbb_tier1_annual' : 'pbb_tier1_semiannual';
      try {
        const r = await plansStripe.checkout(key);
        window.location.href = r.checkout_url;
      } catch (err) {
        toast({ title: 'Checkout failed', description: err?.response?.data?.detail || 'Try again' });
      }
      return;
    }
    setOpenPkg(t.id);
  };

  const byId = (id) => packages.find(p => p.id === id);
  const headBtn = (t, label) => t && (
    <button key={t.id} type="button" onClick={() => buy(t)} data-testid={`compare-buy-${t.id}`}
      className="mt-1 w-full px-3 py-1.5 rounded-full text-[11px] font-semibold normal-case bg-green-600 hover:bg-green-500 text-white whitespace-nowrap">
      {label}
    </button>
  );
  const headCol = (name, price, btns) => (
    <div className="flex flex-col items-center gap-0.5 min-w-[120px]">
      <span className="text-neutral-900 font-bold">{name}</span>
      {price && <span className="text-[11px] font-normal normal-case text-neutral-500">{price}</span>}
      {btns}
    </div>
  );
  const t1 = byId('basic'), t1a = byId('basic_annual'), t2 = byId('standard'), t3 = byId('featured');
  const compareHeaders = [
    headCol('Tier 1', 'Category Listing', [
      headBtn(t1, t1 && `$${t1.price} / ${t1.months} mo`),
      headBtn(t1a, t1a && `$${t1a.price} / ${t1a.months} mo`),
    ]),
    headCol(t2?.name || 'Business Growth', t2 && `$${t2.price} for ${t2.months} mo`, headBtn(t2, t2 && `Buy $${t2.price}`)),
    headCol(t3?.name || 'Full Growth Campaign', t3 && `$${t3.price} for ${t3.months} mo`, headBtn(t3, t3 && `Buy $${t3.price}`)),
  ];

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400 gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading plans…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 py-12" data-testid="public-plans-page">
      <div className="max-w-6xl mx-auto px-4 space-y-8">
        {/* Hero */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-600 text-white text-xs font-semibold uppercase tracking-wider">
            <Sparkles size={12} /> {user ? 'Activate your business plan' : 'Buy a plan first — no signup required'}
          </div>
          {needsSub && (
            <div className="mt-4 mx-auto max-w-xl rounded-xl border border-yellow-500/50 bg-yellow-50 px-4 py-3 text-sm font-medium text-neutral-900" data-testid="plans-subscription-required">
              A subscription is required to create your business listing. Choose a plan to get started.
            </div>
          )}
          <h1 className="mt-4 text-4xl md:text-6xl font-black tracking-tight">Choose a Plan</h1>
          <p className="mt-3 text-neutral-600 max-w-2xl mx-auto text-lg">
            {user
              ? "Pick the tier that fits and pay securely. You'll go straight to creating your business listing."
              : 'Pick the tier that fits, pay securely with PayPal, then create your account. Your plan will automatically apply to the first business you register.'}
          </p>
        </div>

        <PayPalTrustBadge />

        {/* Package cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="public-plans-grid">
          {packages.map(t => {
            const Ic = ICONS[t.id] || Award;
            const highlight = t.recommended;
            return (
              <div key={t.id} data-testid={`plan-card-${t.id}`}
                className={`bg-white rounded-2xl p-6 flex flex-col relative shadow-lg border ${
                  highlight ? 'border-green-500 ring-2 ring-green-500/40 shadow-green-500/20' : 'border-neutral-200'
                }`}>
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-green-500 text-black text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1">
                    <Star size={10} className="fill-black" /> Recommended
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Ic className="text-green-600" size={22} />
                  <div className="font-bold text-lg">{t.name}</div>
                </div>
                <div className="mt-3 text-4xl font-black">
                  ${t.price}
                  <span className="text-sm font-medium text-neutral-500"> / {t.months === 1 ? 'mo' : `${t.months} mo`}</span>
                </div>
                {t.monthlyEquivalent && (
                  <div className="text-sm text-green-700 font-semibold">≈ ${t.monthlyEquivalent}/month</div>
                )}
                {t.regularPrice && t.regularPrice !== t.price && (
                  <div className="text-xs text-neutral-500">then ${t.regularPrice}/mo</div>
                )}
                <ul className="mt-4 space-y-1.5 text-sm text-neutral-700 flex-1">
                  {t.features.slice(0, 8).map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className="text-green-600 mt-0.5 shrink-0" /> {featureLabel(f)}
                    </li>
                  ))}
                  {t.features.length > 8 && (
                    <li className="text-xs text-green-600 pl-6">+ {t.features.length - 8} more</li>
                  )}
                </ul>
                <button
                  type="button"
                  data-testid={`plan-buy-${t.id}`}
                  onClick={() => buy(t)}
                  className={`mt-5 w-full px-3 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 ${
                    highlight ? 'bg-green-500 hover:bg-green-400 text-black' : 'bg-neutral-900 hover:bg-neutral-800 text-white'
                  }`}>
                  {STRIPE_TIER1.has(t.id) ? <><CreditCard size={14}/> Subscribe ${t.price}</> : <>Buy for ${t.price} <ArrowRight size={14} /></>}
                </button>
              </div>
            );
          })}
        </div>

        <PlanCompareTable theme="light" headers={compareHeaders} title="Compare plans side by side" popular={1} tabLabels={['Tier 1', 'Growth', 'Full']} />

        <PlanFAQ byId={byId} />

        <div className="text-center text-sm text-neutral-500">
          Already have an account? <Link to="/login" className="text-green-700 underline">Log in</Link>
        </div>
      </div>

      {/* Checkout modal */}
      {openPkg && current && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
             onClick={() => setOpenPkg(null)} data-testid="guest-checkout-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="font-bold text-xl text-neutral-900">Checkout</div>
            <div className="text-sm mt-1 text-neutral-600">{current.name}</div>
            <div className="mt-3 rounded-xl bg-green-50 border border-green-200 p-3 text-sm space-y-1 text-neutral-800">
              <div className="flex justify-between"><span className="text-neutral-500">Pay now:</span><b className="text-green-700">${current.price}</b></div>
              {current.regularPrice && current.regularPrice !== current.price && (
                <div className="flex justify-between text-xs"><span className="text-neutral-500">After {current.months} months:</span><b>${current.regularPrice} / mo</b></div>
              )}
            </div>
            <div className="mt-3 text-xs text-neutral-500 leading-relaxed">
              After paying you'll be sent to a quick signup form to create your account. Your plan will
              auto-apply to the first business you register.
            </div>
            <div className="mt-5">
              <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD', intent: 'capture' }}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
                  createOrder={async () => {
                    try {
                      const res = await plansGuest.createOrder(openPkg);
                      return res.orderId;
                    } catch (err) {
                      toast({ title: 'PayPal error', description: err?.response?.data?.detail || 'Try again' });
                      throw err;
                    }
                  }}
                  onApprove={async (data) => {
                    try {
                      const res = await plansGuest.capture(data.orderID, openPkg);
                      onPaymentSuccess(res);
                    } catch (err) {
                      toast({ title: 'Capture failed', description: err?.response?.data?.detail || 'Try again' });
                    }
                  }}
                  onError={(err) => toast({ title: 'PayPal error', description: String(err?.message || err) })}
                />
              </PayPalScriptProvider>
            </div>
            <button type="button" onClick={() => setOpenPkg(null)}
              className="mt-4 w-full px-3 py-2 rounded-lg border border-neutral-300 text-sm text-neutral-600 hover:bg-neutral-100">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Duplicated from UpgradePlan.jsx (kept local so this page stays self-contained
// and public-safe — no imports that pull in dashboard-only chunks).
function featureLabel(f) {
  const map = {
    businesses_1: '1 business, 1 category listing',
    businesses_2: 'Up to 2 businesses',
    businesses_4: 'Up to 4 businesses',
    categories_3: 'Up to 3 category listings per business',
    categories_4: 'Up to 4 category listings per business',
    business_photos_3: '3 business photos per listing',
    links_3: '3 links per listing (website + social media combined)',
    business_photos_unlimited: 'Unlimited business photos',
    website_and_social_unlimited: 'Unlimited website + social links',
    signup_form: 'Custom sign-up form',
    survey_builder: 'Customer survey',
    directory_listing: 'Directory listing',
    business_category: 'Business category',
    business_name: 'Business name',
    contact_info: 'Contact information',
    location: 'Location',
    business_photo: 'Business photo',
    business_description: 'Business description',
    website_social: 'Website / Social link',
    business_hours: 'Business hours',
    coupons: 'Coupons',
    loyalty: 'Loyalty card',
    pbb_points: 'PBB points',
    can_participation: 'CAN participation',
    online_market: 'Online market',
    events: 'Events',
    pbb_promotions: 'PBB promotions',
    consumer_engagement: 'Consumer engagement',
    crm: 'Merchant CRM',
    customer_db: 'Customer database',
    segmentation: 'Customer segmentation',
    activity_tracking: 'Activity tracking',
    ai_insights: 'AI customer insights',
    ai_email_sms: 'AI email / SMS',
    automation: 'Automation',
  };
  return map[f] || f.replaceAll('_', ' ');
}
